import { EVENTS, Joyride, STATUS, type ButtonType, type EventData } from "react-joyride";
import { useCallback, useMemo } from "react";

import { TOURS } from "@/lib/guidance/tours.config";
import type { TTourId } from "@/lib/guidance/types";
import { useTour } from "@/hooks/useTour";

type TTourControllerProps = {
  tourId: TTourId;
};

const JOYRIDE_LOCALE = {
  back: "Back",
  close: "Close",
  last: "Finish",
  next: "Next",
  skip: "Skip tour",
};

const JOYRIDE_OPTIONS = {
  skipBeacon: true,
  showProgress: true,
  buttons: ["back", "skip", "primary"] as ButtonType[],
  closeButtonAction: "skip" as const,
  overlayClickAction: "close" as const,
  primaryColor: "hsl(var(--primary))",
  textColor: "hsl(var(--foreground))",
  backgroundColor: "hsl(var(--card))",
  arrowColor: "hsl(var(--card))",
  overlayColor: "rgba(0, 0, 0, 0.45)",
  zIndex: 10000,
};

const ROUNDED_BUTTON_STYLE = {
  borderRadius: 9999,
  paddingLeft: 16,
  paddingRight: 16,
};

const JOYRIDE_STYLES = {
  tooltip: {
    borderRadius: 12,
    padding: 16,
  },
  buttonPrimary: ROUNDED_BUTTON_STYLE,
  buttonBack: ROUNDED_BUTTON_STYLE,
  buttonSkip: ROUNDED_BUTTON_STYLE,
};

export function TourController({ tourId }: TTourControllerProps) {
  const { completeTour, skipTour } = useTour();
  const tour = TOURS[tourId];

  const steps = useMemo(() => {
    return tour.steps.filter((step) => {
      if (typeof document === "undefined") {
        return true;
      }
      const target = step.target;
      if (typeof target !== "string") {
        return true;
      }
      return document.querySelector(target) !== null;
    });
  }, [tour.steps]);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (data.type !== EVENTS.TOUR_END) {
        return;
      }

      if (data.status === STATUS.FINISHED) {
        completeTour(tourId);
      } else if (data.status === STATUS.SKIPPED) {
        skipTour(tourId);
      }
    },
    [completeTour, skipTour, tourId]
  );

  if (steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run
      continuous
      scrollToFirstStep
      locale={JOYRIDE_LOCALE}
      onEvent={handleEvent}
      options={JOYRIDE_OPTIONS}
      styles={JOYRIDE_STYLES}
    />
  );
}
